import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { AppComponent } from './app.component';
import { routing } from './app.routes';
import { LoginComponent } from './components/login/login.component';
import { ForbiddenComponent } from './components/login/forbidden/forbidden.component';
import { LegacyBypassComponent } from './components/login/legacyBypass.component';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { TextMaskModule } from 'angular2-text-mask';
import { CoreModule } from '@sc/pas-common';
import { PipesModule } from './shared/pipes/pipes.module';
import { FaqComponent } from './components/faq/faq.component';
import { AccordionGroupComponent } from './components/shared/accordion/accordion-group.component';
import { AccordionComponent } from './components/shared/accordion/accordion.component';
import { SharedComponentsModule } from './components/shared/shared-components.module';
import { ClaimModule } from './components/claim/claim.module';
import { LegacyModule } from './components/legacy/legacy.module';
import { FaqService } from './components/faq/faq.service';
import { BranchSelectorComponent } from './components/branch-selector/branch-selector.component';
import { MaintenanceComponent } from './components/login/maintenance/maintenance.component';
import { JwtModule } from '@auth0/angular-jwt';
import { AuthGuardService } from '@sc/pas-common';
import { GlobalHeaderInterceptor } from './core/services/global-header.interceptor';
import { DatePipe, TitleCasePipe } from '@angular/common';
import { AutoPlanModule } from './components/auto-plan/auto-plan.module';
import { PasSelectorModule, LateralMenuWrapperModule, ListSetModule } from '@sc/portal.fe.lib.app-components';
import {
    AlertModule,
    ButtonModule,
    DirectivesModule,
    IconModule,
    InputModule,
    ModalModule,
    ScCurrencyPipe,
    ScTabModule,
    SelectModule,
    TitleModule
} from '@sc/portal.fe.lib.ui-core-components';
import { NewsComponent } from './components/news/news.component';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { SeachPolicyComponent } from './components/search-policy/search-policy.component';
import { AllQuotationsComponent } from './components/all-quotations/all-quotations.component';
import { RenovationsComponent } from './components/renovations/renovations.component';
import { ClientsDebtsComponent } from './components/clients-debts/clients-debts.component';
import { PaymentsComponent } from './components/payments/payments.component';
import { DashboardService } from './components/dashboard/dashboard.service';
import { CarQuoteLiteStorageService } from './core/services/car-quote-lite-storage.service';
import { SingleSignatureCreditComponent } from './components/single-signature-credit/single-signature-credit.component';
import { ImpersonatedLoginComponent } from './components/login/impersonated-login/impersonated-login.component';
import { FeatureToggleService } from '@sc/pas-common';
import { SupportedBrowsersModalComponent } from './components/supported-browsers-modal/supported-browsers-modal.component';
import { HomeService } from '@sc/pas-common';
import { loadEnvironmentFactory } from '../environments/loadEnvironment.factory';
import { OldCarQuotationModule } from './modules/old-car-quotation/old-car-quotation.module';
import { PasCommonModule } from '@sc/pas-common';
import { CategorySelectorComponent } from './components/category-selector/category-selector.component';
import { RetirementInsuranceComponent } from './components/retirement-insurance/retirement-insurance.component';
import { SupportPasComponent } from './components/support-pas/support-pas.component';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from 'environments/environment';

export function tokenGetter() {
    return localStorage.getItem('tk');
}

@NgModule({
    imports: [
        PasCommonModule,
        AlertModule.forRoot(),
        InputModule,
        SelectModule,
        ScTabModule,
        ButtonModule,
        ModalModule,
        TitleModule,
        BrowserAnimationsModule,
        BrowserModule,
        CoreModule,
        DirectivesModule,
        FormsModule,
        HttpClientModule,
        ReactiveFormsModule,
        routing,
        PipesModule,
        TextMaskModule,
        RouterModule,
        SharedComponentsModule,
        ClaimModule,
        LegacyModule,
        PasSelectorModule,
        ListSetModule,
        LateralMenuWrapperModule,
        OldCarQuotationModule,
        JwtModule.forRoot({
            config: {
                tokenGetter: tokenGetter,
                whitelistedDomains: [
                    'api-dev.sancristobalonline.com.ar',
                    'api-aks.sancristobalonline.com.ar',
                    'api-dev.sancristobalonline.com.ar',
                    'api-qa.sancristobalonline.com.ar',
                    'api-aks-uat.sancristobalonline.com.ar',
                    'api-prod-aks-centralus.sancristobal.com.ar',
                    'api-prod-aks-eastus2.sancristobal.com.ar',
                    'api.sancristobal.com.ar',
                    'edge-api-stage.azure-api.net',
                    'api-staging.sancristobalonline.com.ar',
                    'edge-api-uat.azure-api.net',
                    'api-uat.sancristobalonline.com.ar',
                    'edge-api.azure-api.net',
                    'api-prod-centralus2.sancristobal.com.ar',
                    'edge-api-eastus2.azure-api.net',
                    'api-prod-eastus2.sancristobal.com.ar',
                    'localhost:5000',
                    'localhost:5001',
                    'localhost:5002',
                    'localhost:5003',
                    'localhost:5005',
                    'localhost:5006',
                    'localhost:5007',
                    'localhost:5009',
                    'localhost:5010',
                    'localhost:5011',
                    'localhost:5020',
                    'localhost:5021',
                    'localhost:5027',
                    'localhost:5030',
                    'localhost:5033',
                    'localhost:5036',
                    'localhost:5040',
                    'localhost:44385',
                    'localhost:5200',
                    'localhost:5204'
                ]
            }
        }),
        AutoPlanModule,
        IconModule,
      StoreModule.forRoot({}),
      StoreDevtoolsModule.instrument({
        maxAge: 25, // Retains last 25 states
        logOnly: environment.production, // Restrict extension to log-only mode
      }),
    ],
    entryComponents: [
        AccordionGroupComponent,
        AccordionComponent
    ],
    declarations: [
        AppComponent,
        ForbiddenComponent,
        LegacyBypassComponent,
        LoginComponent,
        FaqComponent,
        AccordionGroupComponent,
        AccordionComponent,
        BranchSelectorComponent,
        CategorySelectorComponent,
        MaintenanceComponent,
        NewsComponent,
        DashboardComponent,
        SeachPolicyComponent,
        AllQuotationsComponent,
        RenovationsComponent,
        ClientsDebtsComponent,
        PaymentsComponent,
        SingleSignatureCreditComponent,
        ImpersonatedLoginComponent,
        SupportedBrowsersModalComponent,
        RetirementInsuranceComponent,
        SupportPasComponent
    ],
    providers: [
        {
            provide: APP_INITIALIZER,
            useFactory: loadEnvironmentFactory,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: GlobalHeaderInterceptor,
            multi: true
        },
        { provide: 'Window',  useValue: window },
        DatePipe,
        TitleCasePipe,
        ScCurrencyPipe,
        AuthGuardService,
        FaqService,
        HomeService,
        CarQuoteLiteStorageService,
        DashboardService,
        FeatureToggleService,
    ],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule {
}
